/**
 * @file guardianAPI.js
 * API Client methods for managing Money Guardian (Spend & Credit) Safeguard rules.
 */

import { apiFailureFromResponse } from "../../utils/api/parseApiErrorMessage";
import { ApiClient } from "../ApiClient";

const GUARDIAN_RULES_ENDPOINT = "finances/guardian-transactions-rules";
const CREDIT_RULES_ENDPOINT = "finances/guardian-credit-rules";

/**
 * Fetch all guardian transaction rules (defaults and personalized)
 * @returns {Promise<Object>} Object containing separated and mapped rules arrays
 */
export const fetchGuardianRules = async () => {
  const response = await ApiClient.get(GUARDIAN_RULES_ENDPOINT);
  if (response.ok) {
    const result = await response.json();
    const rawRules = result.rules || { default: [], personalized: [] };

    // Normalize both rule lists to cleanly match front-end conventions
    const transformedDefaults = (rawRules.default || []).map((rule) => ({
      uuid: rule.uuid,
      name: rule.name,
      description: rule.description,
      amount: parseFloat(rule.amount) || 0,
      isEnable: rule.is_enable,
      isDefault: true,
    }));

    const transformedPersonalized = (rawRules.personalized || []).map(
      (rule) => ({
        uuid: rule.uuid,
        name: rule.name,
        description: rule.description,
        amount: parseFloat(rule.amount) || 0,
        isEnable: rule.is_enable,
        isDefault: false,
        institutions: rule.institutions || [],
        accounts: rule.accounts || [],
        categories: rule.categories || [],
      }),
    );

    return {
      success: true,
      data: {
        defaultRules: transformedDefaults,
        personalizedRules: transformedPersonalized,
      },
      status: response.status,
    };
  }

  return apiFailureFromResponse(response, "Failed to load transaction rules");
};

/**
 * Create a new standalone custom transaction rule
 * @param {Object} ruleData payload containing name, description, amount, etc.
 */
export const createPersonalizedRule = async (ruleData) => {
  const payload = {
    name: ruleData.name,
    description: ruleData.description,
    amount: ruleData.amount,
    is_enable: ruleData.isEnable ?? true,
    default_rule_id: ruleData.defaultRuleId || null,
    institution_ids: ruleData.institutions || [],
    account_ids: ruleData.accounts || [],
    categories: ruleData.categories || [],
  };

  const response = await ApiClient.post(GUARDIAN_RULES_ENDPOINT, payload);

  if (response.ok) {
    const result = await response.json();
    const rule = result.rule || {};

    const newRule = {
      id: rule.id,
      name: rule.name,
      description: rule.description,
      amount: parseFloat(rule.amount) || 0,
      isEnable: rule.is_enable,
      isDefault: false,
      institutions: rule.institutions || [],
      accounts: rule.accounts || [],
      categories: rule.categories || [],
    };

    return { success: true, data: newRule, status: response.status };
  }

  return apiFailureFromResponse(
    response,
    "Failed to create personalized rule. Please try again.",
  );
};

/**
 * Update an existing personalized rule or manage a baseline rule toggle override
 * @param {string} ruleUuid Target rule unique identifier
 * @param {Object} updateData Partial payload fields including system operational context type
 */
export const updateRule = async (ruleUuid, updateData) => {
  const payload = {};

  if (updateData.type !== undefined) payload.type = updateData.type;
  if (updateData.name !== undefined) payload.name = updateData.name;
  if (updateData.description !== undefined)
    payload.description = updateData.description;
  if (updateData.amount !== undefined) payload.amount = updateData.amount;
  if (updateData.isEnable !== undefined)
    payload.is_enable = updateData.isEnable;
  if (updateData.defaultRuleId !== undefined)
    payload.default_rule_id = updateData.defaultRuleId;
  if (updateData.institutions !== undefined)
    payload.institution_ids = updateData.institutions;
  if (updateData.accounts !== undefined)
    payload.account_ids = updateData.accounts;
  if (updateData.categories !== undefined)
    payload.categories = updateData.categories;

  const response = await ApiClient.put(
    `${GUARDIAN_RULES_ENDPOINT}/${ruleUuid}`,
    payload,
  );

  if (response.ok) {
    const result = await response.json();
    const rule = result.rule || {};
    return {
      success: true,
      data: {
        uuid: rule.uuid,
        name: rule.name,
        description: rule.description,
        amount: parseFloat(rule.amount) || 0,
        isEnable: rule.is_enable,
        isDefault: !!rule.default_rule,
      },
      status: response.status,
    };
  }

  return apiFailureFromResponse(
    response,
    "Failed to preserve transaction rule modification.",
  );
};

/**
 * Delete a personalized custom rule or clear a baseline override link
 * @param {string} ruleUuid Target custom rule identifier
 */
export const deletePersonalizedRule = async (ruleUuid) => {
  const response = await ApiClient.delete(
    `${GUARDIAN_RULES_ENDPOINT}/${ruleUuid}`,
  );

  if (response.ok) {
    return { success: true, status: response.status };
  }

  return apiFailureFromResponse(
    response,
    "Failed to remove custom safeguard rule.",
  );
};

/**
 * Fetches all active credit safeguard rules configured by the authenticated user.
 *
 * @async
 * @returns {Promise<{success: boolean, data?: Array<Object>, error?: string, status: number}>}
 */
export const fetchCreditRules = async () => {
  const response = await ApiClient.get(CREDIT_RULES_ENDPOINT);
  if (response.ok) {
    const result = await response.json();
    const rawRules = result.rules || [];

    const transformed = rawRules.map((rule) => ({
      uuid: rule.uuid,
      name: rule.name,
      description: rule.description,
      amount: parseFloat(rule.amount) || 0,
      isEnable: rule.is_enable,
      accounts: rule.accounts || [],
    }));

    return { success: true, data: transformed, status: response.status };
  }

  return apiFailureFromResponse(response, "Failed to load credit rules.");
};

/**
 * Submits a request to persist a new credit safeguard rule.
 *
 * @async
 * @param {Object} ruleData Payload parameters
 * @param {string} ruleData.name User-defined descriptive rule name
 * @param {number} ruleData.amount Balance dollar amount trigger threshold
 * @param {string} [ruleData.description] Optional details/notes
 * @param {Array<string>} [ruleData.accounts] List of specific account UUIDs to monitor
 * @returns {Promise<{success: boolean, data?: Object, error?: string, status: number}>}
 */
export const createCreditRule = async (ruleData) => {
  const payload = {
    name: ruleData.name,
    description: ruleData.description,
    amount: ruleData.amount,
    is_enable: ruleData.isEnable ?? true,
    account_ids: ruleData.accounts || [],
  };

  const response = await ApiClient.post(CREDIT_RULES_ENDPOINT, payload);

  if (response.ok) {
    const result = await response.json();
    const rule = result.rule || {};

    return {
      success: true,
      data: {
        uuid: rule.uuid,
        name: rule.name,
        description: rule.description,
        amount: parseFloat(rule.amount) || 0,
        isEnable: rule.is_enable,
        accounts: rule.accounts || [],
      },
      status: response.status,
    };
  }

  return apiFailureFromResponse(
    response,
    "Failed to create credit safeguard rule. Please try again.",
  );
};

/**
 * Updates an existing credit safeguard rule.
 *
 * @async
 * @param {string} ruleUuid Unique identifier of the target rule
 * @param {Object} updateData Partial rule update fields
 * @returns {Promise<{success: boolean, data?: Object, error?: string, status: number}>}
 */
export const updateCreditRule = async (ruleUuid, updateData) => {
  const payload = {};

  if (updateData.name !== undefined) payload.name = updateData.name;
  if (updateData.description !== undefined)
    payload.description = updateData.description;
  if (updateData.amount !== undefined) payload.amount = updateData.amount;
  if (updateData.isEnable !== undefined)
    payload.is_enable = updateData.isEnable;
  if (updateData.accounts !== undefined)
    payload.account_ids = updateData.accounts;

  const response = await ApiClient.put(
    `${CREDIT_RULES_ENDPOINT}/${ruleUuid}`,
    payload,
  );

  if (response.ok) {
    const result = await response.json();
    const rule = result.rule || {};

    return {
      success: true,
      data: {
        uuid: rule.uuid,
        name: rule.name,
        description: rule.description,
        amount: parseFloat(rule.amount) || 0,
        isEnable: rule.is_enable,
        accounts: rule.accounts || [],
      },
      status: response.status,
    };
  }

  return apiFailureFromResponse(
    response,
    "Failed to preserve credit safeguard rule modification.",
  );
};

/**
 * Deletes a custom credit safeguard rule.
 *
 * @async
 * @param {string} ruleUuid Unique identifier of the target rule
 * @returns {Promise<{success: boolean, status: number, error?: string}>}
 */
export const deleteCreditRule = async (ruleUuid) => {
  const response = await ApiClient.delete(
    `${CREDIT_RULES_ENDPOINT}/${ruleUuid}`,
  );

  if (response.ok) {
    return { success: true, status: response.status };
  }

  return apiFailureFromResponse(
    response,
    "Failed to remove credit safeguard rule.",
  );
};
